module.exports = function buildWalkForwardTestingTradingStrategies({ agentProofPoints }) {
  const SOURCE_VERIFIED_DATE = '2026-08-14';

  return {
    path: '/guides/walk-forward-testing-trading-strategies',
    title: 'Walk-Forward Testing for Trading Strategies | BotSpot',
    description: 'Learn how walk-forward testing uses ordered training and test windows to evaluate trading strategies while reducing leakage and overfitting risk.',
    pageType: 'article',
    lastmod: SOURCE_VERIFIED_DATE,
    content: {
      eyebrow: 'Trading strategy validation guide',
      heading: 'Use walk-forward testing to measure repeated out-of-sample behavior',
      summary: 'Define a sequence of historical training and test windows, fit or select strategy rules using only each training window, and evaluate the unchanged result on the period that follows. Walk-forward evidence can expose instability, but it cannot prove future performance.',
      primaryAction: { label: 'Plan a strategy test in BotSpot', url: '/ai-agent' },
      secondaryAction: { label: 'Read the backtesting guide', url: '/guides/backtesting-ai-trading-strategies' },
      proofPoints: agentProofPoints,
      sections: [
        {
          heading: '1. Understand what moves forward',
          paragraphs: [
            'Walk-forward testing evaluates a strategy through multiple time-ordered folds. Each fold uses an earlier period for development and the next period for evaluation. The process then advances through history without training on future observations.',
            'Walk-forward testing and walk-forward optimization are related but not identical. Testing can evaluate fixed rules across sequential periods. Optimization adds parameter or model selection inside each training window before applying the selected version to the following test window.',
          ],
          bullets: [
            'Fold 1: train on A–C, then test on D.',
            'Fold 2: train on an allowed window ending at D, then test on E.',
            'Fold 3: train on an allowed window ending at E, then test on F.',
            'Only test-period results count as out-of-sample evidence.',
          ],
        },
        {
          heading: '2. Freeze the protocol before reading results',
          paragraphs: [
            'Choose the training length, test length, step size, optional gap, parameter search space, selection objective, execution model, and stopping rule before comparing outcomes. Changing the protocol after seeing weak folds turns the evaluation process into another optimization.',
            'Test-window length should reflect the intended live decision and retraining schedule. A model intended to update monthly should not claim deployment realism from a schedule that reselects parameters every day.',
          ],
          bullets: [
            'Record every candidate rule and parameter range.',
            'Use the same data definitions, fees, slippage, and order assumptions across folds.',
            'Define minimum sample or trade requirements before excluding a fold.',
            'Preserve run history, including failed and zero-trade results.',
          ],
        },
        {
          heading: '3. Choose anchored or rolling training windows',
          paragraphs: [
            'An anchored, or expanding, window keeps the original start date and adds newly available history at each step. It provides more training observations over time, but old regimes continue influencing every later fit.',
            'A rolling, or sliding, window moves both boundaries forward and keeps a fixed lookback length. It emphasizes recent history and discards older observations, but gives each fit less data.',
            'Neither design is universally better. Choose one based on how the deployed strategy would learn and how quickly its inputs may become stale, then keep that choice fixed during evaluation.',
          ],
          bullets: [
            'Anchored: train A–C → D; train A–D → E; train A–E → F.',
            'Rolling: train A–C → D; train B–D → E; train C–E → F.',
            'Use equal test durations when comparing fold metrics based on elapsed time.',
            'Document warm-up data separately from observations eligible for fitting.',
          ],
        },
        {
          heading: '4. Keep each fold free from future information',
          paragraphs: [
            'Fit normalization, feature selection, imputation, model parameters, thresholds, and universe rules using the training window only. Apply the fitted process to the next test window without learning from that window first.',
            'Add a gap between training and testing when labels, delayed publications, holding periods, or feature calculations overlap the boundary. A gap cannot repair incorrectly timestamped or revised data; inputs still need point-in-time availability.',
            'Earlier test observations may become training data in later folds because they would be known by that later date. They must not influence the earlier fold.',
          ],
          bullets: [
            'Keep rows in chronological order; do not use random train-test splits.',
            'Timestamp inputs when they became available, not only when their reporting period ended.',
            'Build each historical universe from point-in-time membership.',
            'Do not preprocess the complete dataset before creating folds.',
            'Avoid overlapping test windows or account for duplicate dates when combining results.',
          ],
        },
        {
          heading: '5. Reproduce the intended live update policy',
          paragraphs: [
            'When parameters change between folds, record which training observations, candidate values, objective, and tie-breaking rule selected each version. The version chosen from one training window must remain unchanged throughout its next test window.',
            'Frequent optimization can react to newer data, but it also increases compute, turnover, and opportunities to fit noise. QuantConnect documents this tradeoff between recent fitting and overfitting risk.',
            'A production implementation must reproduce historical retraining timing, data availability, warm-up, and state transitions. A walk-forward backtest that updates at impossible times does not represent a deployable process.',
          ],
        },
        {
          heading: '6. Interpret the full out-of-sample sequence',
          paragraphs: [
            'Join non-overlapping test periods in chronological order to inspect the simulated experience produced by the complete update policy. Also report each fold separately so one exceptional interval cannot hide instability.',
            'Compare results with a relevant benchmark over the same dates and under compatible exposure and cost assumptions. Review drawdown, turnover, concentration, trade count, parameter changes, and execution sensitivity alongside returns.',
          ],
          bullets: [
            'Report aggregate out-of-sample results, never the selected in-sample score as validation.',
            'Show fold dispersion, weak periods, and worst drawdown instead of only an average.',
            'Inspect whether chosen parameters remain stable or jump between extremes.',
            'Check whether results depend on one asset, trade, fold, or market regime.',
            'If walk-forward results guide another revision, label them development evidence and reserve a new final holdout or prospective paper period.',
          ],
        },
        {
          heading: 'Limits of walk-forward evidence',
          paragraphs: [
            'Walk-forward testing reduces some weaknesses of one historical split, but it does not eliminate selection bias. Trying many strategies, window designs, objectives, or preprocessing choices can overfit the complete research process.',
            'Short windows can produce noisy estimates. Long windows can mix incompatible regimes. Historical folds still depend on data quality and assumptions about fills, fees, slippage, liquidity, latency, partial fills, and market impact.',
            'Future market structure can differ from every historical window. Walk-forward results remain simulated and do not guarantee profitability, safe deployment, or live execution quality.',
          ],
        },
      ],
      faq: [
        {
          question: 'Is walk-forward testing the same as walk-forward optimization?',
          answer: 'No. Walk-forward testing describes sequential, time-ordered evaluation. Walk-forward optimization additionally selects parameters or logic inside each training window before testing the selected version on the next period.',
        },
        {
          question: 'Should I use an anchored or rolling training window?',
          answer: 'Use the design that matches the intended live learning policy. Anchored windows retain all earlier history; rolling windows keep a fixed recent lookback. Choose before reviewing results and document the tradeoff.',
        },
        {
          question: 'How many walk-forward folds should a strategy use?',
          answer: 'No fixed number proves validity. Use enough chronological folds to evaluate repeated behavior while keeping each training and test window meaningful for the strategy frequency, sample size, and intended update schedule.',
        },
        {
          question: 'Does a successful walk-forward test mean a strategy is ready for live trading?',
          answer: 'No. Results remain historical simulations. Paper testing, broker validation, monitoring, exposure controls, and separate live-deployment review remain necessary.',
        },
      ],
      relatedLinks: [
        { label: 'Backtest AI trading strategies', url: '/guides/backtesting-ai-trading-strategies' },
        { label: 'Understand backtesting metrics', url: '/guides/backtesting-metrics-explained' },
        { label: 'Avoid strategy overfitting', url: '/guides/avoid-overfitting-trading-strategies' },
        { label: 'Prevent look-ahead bias and leakage', url: '/guides/look-ahead-bias-and-data-leakage' },
        { label: 'Compare paper and live trading', url: '/guides/paper-trading-vs-live-trading' },
      ],
      sources: [
        { label: 'BotSpot Terms of Service', url: '/terms' },
        { label: 'Lumibot backtesting documentation', url: 'https://lumibot.lumiwealth.com/backtesting.html' },
        { label: 'QuantConnect walk-forward optimization', url: 'https://www.quantconnect.com/docs/v2/writing-algorithms/optimization/walk-forward-optimization' },
        { label: 'QuantConnect optimization parameters and look-ahead bias', url: 'https://www.quantconnect.com/docs/v2/writing-algorithms/optimization/parameters' },
        { label: 'scikit-learn TimeSeriesSplit documentation', url: 'https://scikit-learn.org/stable/modules/generated/sklearn.model_selection.TimeSeriesSplit.html' },
        { label: 'scikit-learn data leakage guidance', url: 'https://scikit-learn.org/stable/common_pitfalls.html#data-leakage' },
        { label: 'sktime expanding-window documentation', url: 'https://www.sktime.net/docs/api-reference/sktimesplitexpandingwindowexpandingwindowsplitter/' },
        { label: 'sktime sliding-window documentation', url: 'https://www.sktime.net/models/slidingwindowsplitter/' },
      ],
      verifiedAt: SOURCE_VERIFIED_DATE,
    },
  };
};
